import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
} from '@angular/fire/firestore';
import { User } from '../models/user.model';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';
import 'firebase/compat/functions';
import { arrayRemove } from 'firebase/firestore';
import { ROLE_STUDENT, COLLECTION_USERS } from '../global';
import { from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  /**
   *
   * @param db The Firestore instance used to call REST operations.
   */
  constructor(private db: AngularFirestore) { }

  getUser(id: string) {
    return this.db.collection(COLLECTION_USERS).doc(id).get();
  }

  /**
   *
   * @param patientIds The patient IDs to add to the given user.
   * @param userId The ID of the user to add the patient IDs to.
   * @returns Observable
   */
  addPatientIdsToUser(patientIds: string[], userId: string) {
    const updateRef = this.db.collection(COLLECTION_USERS).doc(userId);
    return from(
      updateRef.update({
        patientIds: arrayUnion(...patientIds),
      })
    );
  }

  /**
   *
   * @param patientIds The patient IDs to remove from the given user.
   * @param userId The ID of the user to remove the patient IDs from.
   * @returns Observable
   */
  removePatientIdsFromUser(patientIds: string[], userId: string) {
    const updateRef = this.db.collection(COLLECTION_USERS).doc(userId);
    return from(
      updateRef.update({
        patientIds: arrayRemove(...patientIds),
      })
    );
  }

  /**
   *
   * @param patientIds The patient IDs to set for the given user.
   * @param userId The ID of the user to set the patient IDs for.
   * @returns Observable
   */
  setPatientIdsForUser(patientIds: string[], userId: string) {
    return from(
      this.db.collection(COLLECTION_USERS).doc(userId).set({ patientIds }, { merge: true })
    );
  }

  /**
   * Iterates through all user documents that contain the patient ID
   * and deletes the patient ID from the document's 'patientIds' field.
   *
   * @param id The patient ID to remove from all user documents.
   * @returns Observable
   */
  deletePatientIdFromAllUsers(id: string) {
    return from(
      this.db
        .collection(COLLECTION_USERS)
        .ref.where('patientIds', 'array-contains', id)
        .get()
        .then((snapshot) => {
          snapshot.docs.forEach((doc) => {
            doc.ref.update({
              patientIds: arrayRemove(id),
            });
          });
        })
    );
  }

  /**
   *
   * @returns Observable of QuerySnapshot of all users with the role "student".
   * The Observable completes with the results of the query.
   */
  getAllStudents() {
    const db = getFirestore();
    const q = query(collection(db, COLLECTION_USERS), where('role', '==', ROLE_STUDENT));
    return from(getDocs(q));
  }

  /**
   * Calls a Firebase cloud function to create a user auth account.
   *
   * @param user The user to create in Firestore.
   */
  async createUserAccount(user: User) {
    const createUser = firebase.functions().httpsCallable('createUserV2');
    const remoteFunctionCallRes = await createUser({ user: user })
    user.id = remoteFunctionCallRes.data.uid;
    return user;
  }

  /**
   * Creates a document for the provided user in Firestore.
   *
   * @param user The user to create a document for in Firestore.
   */
  upsertUserDocument(user: User) {
    const obj = this.extractUserData(user);
    return this.db.collection(COLLECTION_USERS).doc(user.id).set(obj);
  }

  deleteStudentIDFromAllUsers(id: string) {
    this.db
      .collection(COLLECTION_USERS)
      .ref.where('studentIds', 'array-contains', id)
      .get()
      .then((snapshot) => {
        snapshot.docs.forEach((doc) => {
          doc.ref.update({
            studentIds: arrayRemove(id),
          });
        });
      });
  }

  /**
   * @param id The ID of the user to delete from Firestore.
   */
  deleteUser(id: string, collection: string) {
    this.db.collection(collection).doc(id).delete();
    const deleteUser = firebase.functions().httpsCallable('deleteUserV2');
    return deleteUser({
      id: id,
    });
  }

  /**
   * Removes the ID parameter from the User object to prepare it for Firestore.
   *
   * @param user The User object to remove the ID from.
   * @returns A copy of the User object with the ID parameter removed.
   */
  private extractUserData(user: User): object {
    // Convert User object to a JSON object
    const obj = JSON.parse(JSON.stringify(Object.assign({}, user)));
    // Do this since the ID will be the document name
    delete obj.id;

    return obj;
  }
}
